'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  useOrgMember,
  useOrgMembers,
  type UseOrgMembersOptions
} from '@/api/queries/useOrgMembers';
import { useDepartments } from '@/api/queries/useDepartments';
import { type UserStatus, type User, type PopulatedUser } from '@bliks-io/contracts';
import {
  cn,
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  getUserInitials,
  getUserFullName,
  getUserAvatarUrl,
  UserName,
  DELETED_ENTITY_CLASS,
  UserCard,
  toUserCardProps
} from '@bliks-io/ui';
import { useTombstoneEntity } from '@/hooks/useTombstoneEntity';
import { DepartmentSelect } from './DepartmentSelect';

export interface UserSelectSettings {
  /**
   * Use compact trigger (just avatar + name) instead of full UserCard
   */
  compactTrigger?: boolean;
}

export interface UserSelectProps {
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  className?: string;
  id?: string;
  name?: string;
  /**
   * Allow clearing the selection (useful for optional fields)
   */
  allowClear?: boolean;
  /**
   * Custom label for the clear/all option
   */
  clearLabel?: string;
  /**
   * Optional callback for custom clear behavior. If not provided, defaults to onChange(undefined)
   */
  onClear?: () => void;
  /**
   * Query options for server-side filtering of users (API level).
   * Defaults to { status: ['inactive', 'pending', 'active'] } to show all assignable users.
   * This allows assigning inactive/pending users for preparation before activation.
   *
   * @default { status: ['inactive', 'pending', 'active'] }
   * @example queryOptions={{ departmentId: 'xyz' }} - Assignable users from specific department
   * @example queryOptions={{ status: ['active'] }} - Only active users (overrides default)
   * @example queryOptions={{ status: undefined }} - All users regardless of status (includes suspended)
   */
  queryOptions?: UseOrgMembersOptions;
  /**
   * Client-side filter function to apply after data is fetched, before internal filters (department dropdown, search).
   * Useful for filtering users by custom criteria that can't be done server-side.
   *
   * @example filter={(user) => user.department?._id === selectedDepartmentId}
   */
  filter?: (user: User) => boolean;
  /**
   * Show the current value even when it's not in the fetched options
   * (deleted or suspended user) — marked in the trigger (deleted grays,
   * suspended suffixes) and appended as a disabled, non-reselectable item at
   * the bottom of the list. Single-select mode only. Edit forms enable this so
   * an existing assignment never renders as a blank field; filters keep it off.
   */
  showUnselectableValue?: boolean;
  /**
   * Shown muted with a "Default" tag while the value is empty. Display only:
   * it is never written to the value. Single-select mode only.
   */
  defaultUser?: PopulatedUser;
  /**
   * UI configuration settings
   */
  settings?: UserSelectSettings;
}

/**
 * Standalone UserSelect component (no Field wrapper)
 * Supports both single and multi-select modes with search and department filtering
 *
 * This is the base component. For form field integration, use FormFieldUserSelect.
 * For React Hook Form, use FormUserSelect.
 *
 * By default, shows inactive, pending, and active users (suitable for assignments where preparation
 * before activation is needed). Suspended users are excluded by default.
 * For admin user management pages, set queryOptions={{ status: undefined }} to show all users.
 */
export const UserSelect = React.forwardRef<HTMLButtonElement, UserSelectProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled = false,
      multiple = false,
      className,
      id,
      name,
      allowClear = false,
      clearLabel,
      onClear,
      queryOptions,
      filter,
      showUnselectableValue = false,
      defaultUser,
      settings
    },
    ref
  ) => {
    const { t } = useTranslation();
    // Merge defaults: assignable statuses + internal classification with provided queryOptions
    const options = {
      status: ['inactive', 'pending', 'active'] as UserStatus[],
      classification: 'internal' as const,
      ...queryOptions
    };
    // External members carry no department, so the department filter can only
    // ever drop them from a list that deliberately includes them. Derived here
    // rather than asked of callers: a caller who opts into externals and
    // forgets to disable the filter would silently lose exactly the people
    // they widened the picker for.
    // `undefined` classification (an explicit override, e.g. the app-seats
    // panel) means every classification — externals included.
    const offersExternalMembers = options.classification?.includes('external') ?? true;
    const { data: users, isLoading, isError } = useOrgMembers(options);
    const { data: departments } = useDepartments();

    // Resolve a current value missing from the filtered options (deleted or
    // suspended user) so edit forms can still display it. The detail endpoint
    // fetches with includeDeleted, so tombstones carry deletedAt/status.
    const tombstoneUser = useTombstoneEntity(
      showUnselectableValue && !multiple,
      value,
      users,
      useOrgMember
    );

    const [departmentFilter, setDepartmentFilter] = React.useState<string | undefined>(undefined);
    const [searchQuery, setSearchQuery] = React.useState('');

    const fieldId = id || name;
    const isDisabled = disabled || isLoading || isError || !users || users.length === 0;

    // Determine placeholder text based on loading/error states
    let placeholderText = placeholder || t('userSelect.placeholder');
    if (isLoading) {
      placeholderText = t('userSelect.loading');
    } else if (isError || !users) {
      placeholderText = t('userSelect.error');
    } else if (users.length === 0) {
      placeholderText = t('userSelect.noUsers');
    }

    // Filter and search users (two-stage filtering)
    const filteredUsers = React.useMemo(() => {
      if (!users) return [];

      // Stage 1: Apply external filter if provided
      let result = users;
      if (filter) {
        result = result.filter(filter);
      }

      // Stage 2: Apply internal filters (department dropdown and search)
      return result.filter((user) => {
        // Department filter
        if (departmentFilter && user.department?._id !== departmentFilter) {
          return false;
        }

        // Search filter
        if (searchQuery) {
          const q = searchQuery.toLowerCase();
          const firstName = user.firstName?.toLowerCase() || '';
          const lastName = user.lastName?.toLowerCase() || '';
          const fullName = getUserFullName(user.firstName, user.lastName).toLowerCase();
          const email = user.email?.toLowerCase() || '';

          if (
            !firstName.includes(q) &&
            !lastName.includes(q) &&
            !fullName.includes(q) &&
            !email.includes(q)
          ) {
            return false;
          }
        }

        return true;
      });
    }, [users, filter, departmentFilter, searchQuery]);

    // Get selected users for display
    const selectedUsers = React.useMemo(() => {
      if (!users || !value) return [];

      const selectedIds = Array.isArray(value) ? value : [value];
      const fromOptions = users.filter((user) => selectedIds.includes(user._id));
      // Fall back to the resolved tombstone so a deleted/suspended current
      // value still renders in the trigger instead of a blank field.
      if (fromOptions.length === 0 && tombstoneUser) {
        return [tombstoneUser];
      }
      return fromOptions;
    }, [users, value, tombstoneUser]);

    // Render trigger content with avatars
    const triggerContent = React.useMemo(() => {
      if (!value && defaultUser && !multiple && !isError) {
        return (
          <div className="flex items-center gap-2">
            <div className="flex-1 opacity-60">
              <UserCard user={toUserCardProps(defaultUser)} variant="with-role" size="xs" />
            </div>
            <Badge colorScheme="neutral">{t('userSelect.default')}</Badge>
          </div>
        );
      }

      if (selectedUsers.length === 0) {
        return null;
      }

      if (!multiple || selectedUsers.length === 1) {
        // When the tombstone resolves it IS the single selected user (the
        // value is missing from options), and live options never carry
        // deletedAt — so the tombstone decorations apply unconditionally.
        const user = selectedUsers[0];

        // Compact trigger - just avatar + name (like SystemSelect)
        if (settings?.compactTrigger) {
          return (
            <div className={cn('flex items-center gap-2', user.deletedAt && DELETED_ENTITY_CLASS)}>
              <Avatar className="h-6 w-6">
                <AvatarImage
                  src={getUserAvatarUrl(user, 'thumbnail')}
                  alt={getUserFullName(user.firstName, user.lastName)}
                />
                <AvatarFallback className="text-xs">
                  {getUserInitials(user.firstName, user.lastName)}
                </AvatarFallback>
              </Avatar>
              <UserName
                name={getUserFullName(user.firstName, user.lastName)}
                user={user}
                className="truncate"
              />
            </div>
          );
        }

        // Default - full UserCard (self-decorates deleted and suspended users)
        return (
          <UserCard user={toUserCardProps(user)} variant="with-role" className="flex-1 p-0 gap-2" />
        );
      }

      // Multiple users selected
      return (
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {selectedUsers.slice(0, 3).map((user) => (
              <Avatar key={user._id} className="h-6 w-6 border-2 border-background">
                <AvatarImage
                  src={getUserAvatarUrl(user, 'thumbnail')}
                  alt={getUserFullName(user.firstName, user.lastName)}
                />
                <AvatarFallback className="text-xs">
                  {getUserInitials(user.firstName, user.lastName)}
                </AvatarFallback>
              </Avatar>
            ))}
          </div>
          <span className="truncate">
            {t('userSelect.usersSelected', { count: selectedUsers.length })}
          </span>
        </div>
      );
    }, [value, defaultUser, isError, selectedUsers, multiple, settings?.compactTrigger, t]);

    // Handle clear option selection
    const handleClearSelect = React.useCallback(() => {
      if (onClear) {
        onClear();
      } else {
        onChange(multiple ? [] : undefined);
      }
    }, [onClear, onChange, multiple]);

    return (
      <Combobox
        value={value}
        onValueChange={onChange}
        disabled={isDisabled}
        multiple={multiple}
        onClear={onClear}
      >
        <ComboboxTrigger
          ref={ref}
          id={fieldId}
          placeholder={placeholderText}
          allowClear={allowClear}
          className={cn('py-1.5', className)}
        >
          {triggerContent}
        </ComboboxTrigger>
        <ComboboxContent className="min-w-[300px]">
          <ComboboxInput
            placeholder={t('userSelect.search')}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          {/* Department filter header */}
          {!offersExternalMembers && departments && departments.length > 0 && (
            <div className="border-b px-3 py-2">
              <DepartmentSelect
                value={departmentFilter}
                onChange={setDepartmentFilter}
                placeholder={t('userSelect.allDepartments')}
                className="h-8 font-normal"
                allowClear
              />
            </div>
          )}
          <ComboboxList>
            <ComboboxEmpty>{t('userSelect.noResults')}</ComboboxEmpty>
            {allowClear && (
              <ComboboxItem
                value="__clear__"
                onSelect={handleClearSelect}
                className="gap-2 items-center py-3"
              >
                <span
                  className={cn(
                    'flex-1 text-muted-foreground',
                    selectedUsers.length === 0 && 'font-semibold'
                  )}
                >
                  {clearLabel || t('userSelect.noManager')}
                </span>
              </ComboboxItem>
            )}
            {filteredUsers.map((user) => (
              <ComboboxItem key={user._id} value={user._id}>
                <UserCard
                  user={toUserCardProps(user)}
                  variant="with-role"
                  className="flex-1 p-0 gap-2"
                />
              </ComboboxItem>
            ))}
            {/* Current value that's deleted/suspended — visible but not reselectable */}
            {tombstoneUser && (
              <ComboboxItem value={tombstoneUser._id} disabled>
                <UserCard
                  user={toUserCardProps(tombstoneUser)}
                  variant="with-role"
                  className="flex-1 p-0 gap-2"
                />
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    );
  }
);

UserSelect.displayName = 'UserSelect';
